import { useContext, useEffect, useRef, useState } from 'react';
import { io } from 'socket.io-client';
import { CartContext } from '../context/CartContext';

const SHARED_CART_CLIENT_ID_KEY = 'sharedCartClientId';

function isTakeaway(tableNo) {
  if (!tableNo) return true;
  const t = String(tableNo).toLowerCase();
  return t === 'take_away' || t === 'take-away';
}

function getOrCreateClientId() {
  let id = sessionStorage.getItem(SHARED_CART_CLIENT_ID_KEY);
  if (!id) {
    id = `c_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 10)}`;
    sessionStorage.setItem(SHARED_CART_CLIENT_ID_KEY, id);
  }
  return id;
}

// 共享购物车 hook
// 参数：restaurantUrl, tableNo, enabled
// 返回：partnerFirstAdd (最近一次"伙伴首次加菜"事件；使用后可调 clearPartnerFirstAdd 清空)
export function useSharedCart(restaurantUrl, tableNo, enabled = true) {
  const { setCartFromRemote, subscribeLocalChange } = useContext(CartContext);
  const [partnerFirstAdd, setPartnerFirstAdd] = useState(null);
  const [isSharedCartReady, setIsSharedCartReady] = useState(false);

  const socketRef = useRef(null);

  useEffect(() => {
    setIsSharedCartReady(false);
    if (!enabled || !restaurantUrl || !tableNo || isTakeaway(tableNo)) {
      setPartnerFirstAdd(null);
      return undefined;
    }

    const clientId = getOrCreateClientId();
    const serverUrl = process.env.REACT_APP_SERVER_URL;
    const socket = io(`${serverUrl}/shared-cart`, {
      transports: ['websocket', 'polling'],
      reconnection: true,
    });
    socketRef.current = socket;

    let active = true;
    let ready = false;
    let snapshot = null;
    let pending = [];
    let inFlight = null;
    let connectionGeneration = 0;
    const retiredEpochs = new Set();

    const acceptSnapshot = (payload, force = false) => {
      if (!payload || !Array.isArray(payload.cart) || !payload.epoch) return false;
      if (retiredEpochs.has(payload.epoch)) return false;
      // An expired Redis key starts at version zero. A stale_cart response is
      // authoritative even then; never accept a late response from a retired batch.
      if (!force && payload.reason !== 'stale_cart' && snapshot && payload.version < snapshot.version) return false;
      if (snapshot && snapshot.epoch !== payload.epoch) retiredEpochs.add(snapshot.epoch);
      snapshot = payload;
      return true;
    };

    // Serialize local operations and apply an authoritative snapshot after the
    // queue drains. An early acknowledgement must not erase later local clicks.
    const pump = () => {
      if (!active || !ready || !socket.connected || inFlight) return;
      if (pending.length === 0) {
        if (snapshot) setCartFromRemote(snapshot.cart);
        setIsSharedCartReady(true);
        return;
      }
      const request = { op: pending.shift(), epoch: snapshot.epoch };
      inFlight = request;
      socket.emit('cart:op', request, (response) => {
        if (!active || inFlight !== request) return;
        inFlight = null;
        if (!response || !response.ok) {
          pending = [];
          if (acceptSnapshot(response)) setCartFromRemote(snapshot.cart);
          else if (snapshot) setCartFromRemote(snapshot.cart);
          setIsSharedCartReady(Boolean(response?.epoch && ready));
          return;
        }
        acceptSnapshot(response);
        pump();
      });
    };

    socket.on('connect', () => {
      ready = false;
      setIsSharedCartReady(false);
      const generation = ++connectionGeneration;
      socket.emit('join', { restaurantUrl, tableNo, clientId }, (response) => {
        if (!active || generation !== connectionGeneration) return;
        if (!response?.ok || !response.epoch) return;
        acceptSnapshot(response, !snapshot);
        ready = true;
        pump();
      });
    });

    socket.on('disconnect', () => {
      ready = false;
      setIsSharedCartReady(false);
      connectionGeneration++;
      // Never replay operations whose acceptance is unknown across reconnects.
      pending = [];
      inFlight = null;
      snapshot = null;
    });

    const receiveSnapshot = (payload) => {
      const previousEpoch = snapshot?.epoch;
      if (!acceptSnapshot(payload)) return;
      if (previousEpoch && previousEpoch !== snapshot.epoch) {
        pending = [];
        inFlight = null;
      }
      if (!inFlight && pending.length === 0) {
        setCartFromRemote(snapshot.cart);
        setIsSharedCartReady(ready);
      }
    };
    socket.on('cart:updated', receiveSnapshot);
    socket.on('cart:cleared', receiveSnapshot);

    socket.on('partner:first-add', (payload) => {
      console.log('[sharedCart] received partner:first-add', payload, 'myClientId=', clientId);
      if (!payload || payload.fromClientId === clientId) {
        console.log('[sharedCart] skipped (self or empty)');
        return;
      }
      if (!payload.mainItem) {
        console.log('[sharedCart] skipped (no mainItem)');
        return;
      }
      console.log('[sharedCart] setting partnerFirstAdd');
      setPartnerFirstAdd({
        mainItem: payload.mainItem,
        ts: Date.now(),
      });
    });

    // 订阅本地 cart 变更 → 转成 op 发给服务器
    const unsubscribe = subscribeLocalChange(({ op }) => {
      if (!op) return;
      setIsSharedCartReady(false);
      pending.push(op);
      pump();
    });

    return () => {
      active = false;
      pending = [];
      if (unsubscribe) unsubscribe();
      socket.removeAllListeners();
      socket.disconnect();
      socketRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [restaurantUrl, tableNo, enabled]);

  const clearPartnerFirstAdd = () => setPartnerFirstAdd(null);

  return { partnerFirstAdd, clearPartnerFirstAdd, isSharedCartReady };
}
